// Unit conversion utilities for Grafty.
// The database always stores weights in kg (weight_kg column).
// The frontend converts to the user's preferred measurement system for display/input.

export type MeasurementSystem = 'imperial' | 'metric'

export const KG_TO_LBS = 2.20462

/** Convert kg → lbs */
export function kgToLbs(kg: number): number {
  return kg * KG_TO_LBS
}

/** Convert lbs → kg */
export function lbsToKg(lbs: number): number {
  return lbs / KG_TO_LBS
}

/** Format a weight for display, converting from the stored kg value. */
export function formatWeight(kg: number, system: MeasurementSystem): string {
  if (system === 'imperial') {
    return `${(kg * KG_TO_LBS).toFixed(1)} lbs`
  }
  return `${kg.toFixed(1)} kg`
}

/** Return the weight unit label for the given system. */
export function weightUnit(system: MeasurementSystem): string {
  return system === 'imperial' ? 'lbs' : 'kg'
}

/** Convert a user-entered weight to kg for storage in the DB.
 *  If the user is on imperial, their input is in lbs — convert to kg.
 *  If metric, their input is already kg. */
export function inputToKg(value: number, system: MeasurementSystem): number {
  return system === 'imperial' ? value / KG_TO_LBS : value
}

/** Convert a stored kg value to the user's preferred display unit (for pre-filling inputs). */
export function kgToDisplayUnit(kg: number, system: MeasurementSystem): number {
  return system === 'imperial' ? kg * KG_TO_LBS : kg
}

// ── Length (DB stores cm) ──────────────────────────────────────────────────────
export const CM_TO_IN = 0.393701

/** Unit label for length inputs/labels (inches imperial, cm metric). */
export function lengthUnit(system: MeasurementSystem): string {
  return system === 'imperial' ? 'in' : 'cm'
}
/** Stored cm → the user's display unit (in for imperial). For pre-filling inputs. */
export function cmToDisplay(cm: number, system: MeasurementSystem): number {
  return system === 'imperial' ? cm * CM_TO_IN : cm
}
/** User-entered length (in for imperial, cm for metric) → cm for storage. */
export function inputToCm(value: number, system: MeasurementSystem): number {
  return system === 'imperial' ? value / CM_TO_IN : value
}
/** Format a stored cm value with unit for display. */
export function formatLength(cm: number, system: MeasurementSystem): string {
  return system === 'imperial' ? `${(cm * CM_TO_IN).toFixed(0)} in` : `${cm.toFixed(0)} cm`
}

// ── Spacing (DB stores metres) — vineyard row/vine spacing (§55.20) ─────────────
export const M_TO_FT = 3.280839895

/** Unit label for spacing inputs (ft imperial, m metric). */
export function spacingUnit(system: MeasurementSystem): string {
  return system === 'imperial' ? 'ft' : 'm'
}
/** Stored metres → the user's display unit (ft for imperial). For pre-filling inputs. */
export function metersToDisplay(m: number, system: MeasurementSystem): number {
  return system === 'imperial' ? m * M_TO_FT : m
}
/** User-entered spacing (ft for imperial, m for metric) → metres for storage. */
export function inputToMeters(value: number, system: MeasurementSystem): number {
  return system === 'imperial' ? value / M_TO_FT : value
}

// ── Area (DB stores acres) ─────────────────────────────────────────────────────
export const ACRE_TO_HA = 0.404686

/** Unit label for area inputs/labels (ac imperial, ha metric). */
export function areaUnit(system: MeasurementSystem): string {
  return system === 'imperial' ? 'ac' : 'ha'
}
/** Stored acres → the user's display unit (ha for metric). For pre-filling inputs. */
export function acresToDisplay(acres: number, system: MeasurementSystem): number {
  return system === 'imperial' ? acres : acres * ACRE_TO_HA
}
/** User-entered area (ac for imperial, ha for metric) → acres for storage. */
export function inputToAcres(value: number, system: MeasurementSystem): number {
  return system === 'imperial' ? value : value / ACRE_TO_HA
}
/** Format a stored acres value with unit for display. */
export function formatArea(acres: number, system: MeasurementSystem): string {
  return system === 'imperial' ? `${acres.toFixed(2)} ac` : `${(acres * ACRE_TO_HA).toFixed(2)} ha`
}

// ── Volume (DB stores liters — e.g. equipment.tank_capacity_liters) ─────────────
export const GAL_TO_L = 3.785411784

/** Unit label for volume inputs/labels (gal imperial, L metric). */
export function volumeUnit(system: MeasurementSystem): string {
  return system === 'imperial' ? 'gal' : 'L'
}
/** Stored liters → the user's display unit (gal for imperial). For pre-filling inputs. */
export function litersToDisplay(liters: number, system: MeasurementSystem): number {
  return system === 'imperial' ? liters / GAL_TO_L : liters
}
/** User-entered volume (gal for imperial, L for metric) → liters for storage. */
export function inputToLiters(value: number, system: MeasurementSystem): number {
  return system === 'imperial' ? value * GAL_TO_L : value
}
/** Format a stored liters value with unit for display. */
export function formatVolume(liters: number, system: MeasurementSystem): string {
  return system === 'imperial' ? `${(liters / GAL_TO_L).toFixed(1)} gal` : `${liters.toFixed(1)} L`
}

// ── Geodesic polygon area (for auto-calculating block/zone acreage) ─────────────
/** Area of a lat/lng ring in **acres** (spherical-Earth approximation — same
 *  algorithm Google Maps uses; accurate to well under 1% at field scale).
 *  Input is the polygon's outer ring as {lat,lng} points (open or closed). */
export function polygonAcres(ring: { lat: number; lng: number }[]): number {
  if (!ring || ring.length < 3) return 0
  const R = 6378137 // Earth radius, meters (WGS84)
  const rad = (d: number) => (d * Math.PI) / 180
  let sum = 0
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]
    const b = ring[(i + 1) % ring.length]
    sum += rad(b.lng - a.lng) * (2 + Math.sin(rad(a.lat)) + Math.sin(rad(b.lat)))
  }
  const areaM2 = Math.abs((sum * R * R) / 2)
  return areaM2 / 4046.8564224 // m² → acres
}

/** Acres of a GeoJSON Polygon / MultiPolygon (outer rings; coords are [lng,lat]). */
export function geojsonPolygonAcres(geometry: GeoJSON.Geometry | null | undefined): number {
  if (!geometry) return 0
  const ringAcres = (ring: number[][]) => polygonAcres((ring || []).map(([lng, lat]) => ({ lat, lng })))
  if (geometry.type === 'Polygon') return ringAcres(geometry.coordinates?.[0] ?? [])
  if (geometry.type === 'MultiPolygon') {
    return (geometry.coordinates ?? []).reduce((s: number, poly) => s + ringAcres(poly?.[0] ?? []), 0)
  }
  return 0
}

// ── Temperature (DB stores °C) ─────────────────────────────────────────────────
/** Unit label for temperature (°F imperial, °C metric). */
export function tempUnit(system: MeasurementSystem): string {
  return system === 'imperial' ? '°F' : '°C'
}
/** Stored °C → display value (°F for imperial). */
export function cToDisplay(c: number, system: MeasurementSystem): number {
  return system === 'imperial' ? c * 9 / 5 + 32 : c
}
/** Format a stored °C value with unit. */
export function formatTemp(c: number, system: MeasurementSystem): string {
  return system === 'imperial' ? `${(c * 9 / 5 + 32).toFixed(0)}°F` : `${c.toFixed(0)}°C`
}
